import React, { useEffect, useRef, useState } from 'react';
import { isSignInWithEmailLink } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { auth, appCheckHeaders } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { getCachedEmail, clearCachedEmail } from '../auth/emailCache';
import { BACKEND_URL } from './api';

const EMAIL_KEY = 'utilibill:tenantEmail';
const BIND_DONE_KEY = 'utilibill:tenantBindDone';

type Phase = 'detecting' | 'need-email' | 'completing' | 'no-link' | 'error';

const cardSx = {
  width: '100%',
  maxWidth: 380,
  bgcolor: 'background.paper',
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  p: 4,
  boxShadow: '0 4px 24px rgba(11,25,41,0.08)',
};

export default function TenantSignIn() {
  const { user, loading, completeEmailLinkSignIn, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('detecting');
  const [emailInput, setEmailInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const initiated = useRef(false);

  useEffect(() => {
    if (loading || initiated.current) return;
    initiated.current = true;

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      if (user) {
        navigate('/tenant', { replace: true });
      } else {
        setPhase('no-link');
      }
      return;
    }

    const stored = getCachedEmail(EMAIL_KEY);
    if (stored) {
      void runSignIn(stored);
    } else {
      setPhase('need-email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const runSignIn = async (email: string) => {
    setPhase('completing');
    try {
      await completeEmailLinkSignIn(email);
      if (!sessionStorage.getItem(BIND_DONE_KEY)) {
        const token = await getIdToken(false);
        if (token) {
          const res = await fetch(`${BACKEND_URL}/tenants/bind`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeaders()) },
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error ?? `Bind failed (${res.status})`);
          }
          sessionStorage.setItem(BIND_DONE_KEY, '1');
        }
      }
      clearCachedEmail(EMAIL_KEY);
      navigate('/tenant', { replace: true });
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Sign-in failed. Please request a new link.');
      setPhase('error');
    }
  };

  if (loading || phase === 'detecting' || phase === 'completing') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Box sx={cardSx}>
        <Typography
          variant="h5"
          sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', mb: 0.5 }}
        >
          Utilibill
        </Typography>

        {phase === 'no-link' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tenant portal
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 2,
                bgcolor: 'rgba(37,99,235,0.04)',
                borderRadius: 2,
                border: '1px solid rgba(37,99,235,0.12)',
              }}
            >
              <EmailOutlinedIcon sx={{ color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  Check your email
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Use the sign-in link your property manager sent you. Need a new one? Contact them directly.
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {phase === 'error' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Sign-in failed
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
            <Typography variant="caption" color="text.secondary">
              Contact your property manager to request a new sign-in link.
            </Typography>
          </>
        )}

        {phase === 'need-email' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Confirm your email to continue
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Email address"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                fullWidth
                required
                autoFocus
                size="small"
              />
              <Button
                variant="contained"
                fullWidth
                onClick={() => void runSignIn(emailInput)}
                disabled={!emailInput}
              >
                Continue
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
